import * as PIXI from 'pixi.js';
import { TypedParentMixin } from '../parentMixin';
import type { ColorGradingFilter } from '../filters/ColorGradingFilter';
import { Config } from '../../video-data/config';
import type { ColorGradingSettings } from '../ColorGradingSettings';
import { TypedUpdateFlowMixin } from '../updateFlowMixin';
import { MediaAsset } from './MediaAsset';

export class ColorGradingManager extends TypedParentMixin(
    TypedUpdateFlowMixin(PIXI.utils.EventEmitter),
) {
    override get parent(): MediaAsset {
        return super.parent as MediaAsset;
    }

    get #gradingSettings(): ColorGradingSettings | null {
        if (this.parent.destroyed) {
            return null;
        }
        return this.parent.gradingOverride || this.parent.root.template.grading;
    }

    #getColorGradingFilter(): ColorGradingFilter | undefined {
        return this.parent.getFilterByName(
            MediaAsset.COLOR_GRADING_FILTER_NAME,
        ) as ColorGradingFilter;
    }

    #getOrCreateColorGradingFilter(): ColorGradingFilter {
        let filter = this.#getColorGradingFilter();
        if (!filter) {
            filter = this.parent.addFilter(
                Config.COLOR_GRADING_FILTER,
                MediaAsset.COLOR_GRADING_FILTER_NAME,
            );
            filter.addListener('onLoaded', this.#emitAssetLoaded);
        }
        return filter;
    }

    #setFilterProperties(): ColorGradingFilter | undefined {
        if (!this.#gradingSettings) {
            return;
        }

        const filter = this.#getOrCreateColorGradingFilter();
        if (filter.textureUrl !== this.#gradingSettings.source) {
            filter.textureUrl = this.#gradingSettings.source;
        }
        if (filter.intensity !== this.#gradingSettings.intensity) {
            filter.intensity = this.#gradingSettings.intensity;
        }
        return filter;
    }

    #emitAssetLoaded = () => {
        this.emit('onLoaded');
    };

    updateFilterProperties(): void {
        if (!this.#gradingSettings) {
            // Clear the filter if there are no grading settings on the template
            const filter = this.#getColorGradingFilter();
            if (filter) {
                this.parent.removeFilter(filter.name);
                filter.destroy();
                filter.removeListener('onLoaded', this.#emitAssetLoaded);
                this.#emitAssetLoaded();
            }
            return;
        }
        this.#setFilterProperties();
    }

    get isLoaded(): boolean {
        if (!this.#gradingSettings) {
            return true; // No grading settings means no filter to load
        }
        const filter = this.#getColorGradingFilter();
        return filter ? filter.isLoaded : true;
    }

    override _update(): void {
        this.updateFilterProperties();
    }
}
